import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CloudOff, UserRound, Users, Wifi } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Modal, touch } from './Modal';
import { apiMessage, switchEmployee } from '../../lib/receiving';

/**
 * The tablet header (spec §2): who is using the tablet, always visible, and the switch. The
 * switch is a real sign-in — a drop-down would let anyone act as anyone.
 */
export function EmployeeBar({
  name,
  roleLabel,
  online,
  pending,
  onSwitched,
  children,
}: {
  name: string;
  roleLabel: string;
  online: boolean;
  pending: number;
  onSwitched: () => void;
  /** The tablet's tabs, on the same row so the card keeps its height. */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await switchEmployee(email.trim(), password);
      setOpen(false);
      setPassword('');
      setEmail('');
      onSwitched();
    } catch (err) {
      setError(apiMessage(err, t('rcv.switch.failed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card px-3 py-2">
      <UserRound className="h-6 w-6 text-muted-foreground" />
      <div className="min-w-0">
        <div className="text-lg font-semibold leading-tight">{name}</div>
        <div className="text-sm text-muted-foreground">{roleLabel}</div>
      </div>
      {children}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {online ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400">
            <Wifi className="h-4 w-4" /> {t('rcv.online')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/20 px-3 py-1.5 text-sm font-medium text-amber-800 dark:text-amber-300">
            <CloudOff className="h-4 w-4" /> {t('rcv.offline')}
          </span>
        )}
        {pending > 0 && (
          <span className="rounded-md bg-sky-500/15 px-3 py-1.5 text-sm font-semibold text-sky-800 dark:text-sky-300">
            {t('rcv.pending', { count: pending })}
          </span>
        )}
        <Button variant="outline" className="min-h-12 px-3 text-base" onClick={() => setOpen(true)}>
          <Users className="h-5 w-5" /> {t('rcv.switch.button')}
        </Button>
      </div>
      <Modal
        open={open}
        title={t('rcv.switch.title')}
        subtitle={t('rcv.switch.subtitle', { name })}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" className={touch} onClick={() => setOpen(false)}>
              {t('rcv.cancel')}
            </Button>
            <Button
              className={touch}
              disabled={busy || !email || !password}
              onClick={() => void submit()}
            >
              {t('rcv.switch.signIn')}
            </Button>
          </>
        }
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div>
            <Label htmlFor="sw-email" className="text-base">
              {t('rcv.switch.email')}
            </Label>
            <Input
              id="sw-email"
              type="email"
              autoComplete="username"
              className="h-12 text-lg"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="sw-pass" className="text-base">
              {t('rcv.switch.password')}
            </Label>
            <Input
              id="sw-pass"
              type="password"
              autoComplete="current-password"
              className="h-12 text-lg"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error && (
            <p className="text-base text-destructive" role="alert">
              {error}
            </p>
          )}
          <button type="submit" hidden />
        </form>
      </Modal>
    </div>
  );
}
